import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import fileSaver from 'file-saver';
import SelectField from 'material-ui/SelectField';
import MenuItem from 'material-ui/MenuItem';
import { Typography, Box } from '@material-ui/core';

import InnerContent from '../../components/PageStructure/InnerContent/InnerContent';
import PageContent from './../../components/PageStructure/PageContent/PageContent';
import PageHeader from './../../components/PageStructure/PageHeader/PageHeader';
import ReportForm from './ReportForm';
import { dropDownStyle, common } from '../../theme/AllocateBaseTheme';
import { queryStringBuilder, lowerFirstLetter } from '../../utils/helpers';
import globals from '../../globals/communityGlobals';
import _ from '../../utils/lodash';

const locale = globals.getArea('reports');
const reportNamesLocale = globals.getArea('reportNames');
const queryBase = `${globals.urls.apiUrl}/v1/query/GenerateReport`;

const ReportSelection = props => {
  const auth = useSelector(state => state.global.auth);
  const jwtToken = auth.jwtToken;

  const { selectedReportTypeName, selectReportTypeName } = props;

  const [isGenerating, setIsGenerating] = useState();
  const [reportsInProgress, setReportsInProgress] = useState([]);
  const [finishedReports, setFinishedReports] = useState([]);

  useEffect(() => {
    const finishedReport = finishedReports.find(({ url }) => !reportsInProgress.includes(url));
    const stillInProg = finishedReport
      ? reportsInProgress.filter(r => r.url !== finishedReport.url)
      : reportsInProgress;
    setReportsInProgress(stillInProg);
    setFinishedReports([]);
    setIsGenerating(stillInProg.some(x => x.reportName === selectedReportTypeName));
  }, [finishedReports.length > 0]);

  const addReportToGeneratingList = (reportName, url) => {
    if (!reportsInProgress.some(r => r.url === url)) {
      setReportsInProgress([...reportsInProgress, { reportName, url }]);
    }
    setIsGenerating(true);
  };

  const addReportToFinishedList = (reportName, url) => {
    setFinishedReports([...finishedReports, { reportName, url }]);
  };

  const generateReport = reportData => {
    const reportName = reportData.reportType;
    const url = `${queryBase}${queryStringBuilder(reportData)}`;
    addReportToGeneratingList(reportName, url);
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.setRequestHeader('Authorization', `Bearer ${jwtToken}`);
    xhr.responseType = 'blob';
    xhr.onload = () => {
      if (xhr.status !== 200) {
        addReportToFinishedList(reportName, url);
        return;
      }
      const blob = xhr.response;
      const now = globals.now.format('YYYY-MM-DD');
      fileSaver.saveAs(blob, `${reportNamesLocale.getMsg(lowerFirstLetter(reportName))}(${now}).xlsx`);
      addReportToFinishedList(reportName, url);
    };
    xhr.send(null);
  };

  const otherReportIsSelected = report => {
    if (selectedReportTypeName && selectedReportTypeName !== report) {
      const isAlreadyGenerating = reportsInProgress.some(r => r.reportName === report);
      setIsGenerating(isAlreadyGenerating);
    }
  };

  const handleFilterChange = reportData => {
    const url = queryBase + queryStringBuilder(reportData);
    setIsGenerating(reportsInProgress && reportsInProgress.some(r => r.url === url));
  };

  const possibleFilters = {
    teams: true,
    dateRange: true,
    period: true,
    clinicians: true,
    nhsNumber: true,
    activity: false,
    referralServices: false,
  };

  const reportForms = {
    ActualizationReport: (
      <ReportForm
        generateReport={generateReport}
        filters={possibleFilters}
        name={selectedReportTypeName}
        handleFilterChange={handleFilterChange}
        isGenerating={isGenerating}
      />
    ),
    CliniciansUtilizationReport: (
      <ReportForm
        generateReport={generateReport}
        filters={{ ...possibleFilters, nhsNumber: false }}
        name={selectedReportTypeName}
        handleFilterChange={handleFilterChange}
        isGenerating={isGenerating}
      />
    ),
    PlannedVsUnplannedActivitiesReport: (
      <ReportForm
        generateReport={generateReport}
        filters={possibleFilters}
        name={selectedReportTypeName}
        handleFilterChange={handleFilterChange}
        isGenerating={isGenerating}
      />
    ),
    RescheduledVisitsReport: (
      <ReportForm
        generateReport={generateReport}
        filters={{ ...possibleFilters, clinicians: false }}
        name={selectedReportTypeName}
        handleFilterChange={handleFilterChange}
        isGenerating={isGenerating}
      />
    ),
    OpenReferralsReport: (
      <ReportForm
        generateReport={generateReport}
        filters={{ ...possibleFilters, period: false, clinicians: false, referralServices: true }}
        name={selectedReportTypeName}
        handleFilterChange={handleFilterChange}
        isGenerating={isGenerating}
      />
    ),
    OpenTreatmentsReport: (
      <ReportForm
        generateReport={generateReport}
        filters={{ ...possibleFilters, clinicians: false }}
        name={selectedReportTypeName}
        handleFilterChange={handleFilterChange}
        isGenerating={isGenerating}
      />
    ),
    CanceledVisitsReport: (
      <ReportForm
        generateReport={generateReport}
        filters={{ ...possibleFilters, clinicians: false }}
        name={selectedReportTypeName}
        handleFilterChange={handleFilterChange}
        isGenerating={isGenerating}
      />
    ),
    ActivityVarianceReport: (
      <ReportForm
        generateReport={generateReport}
        filters={{ ...possibleFilters, activity: true, nhsNumber: false }}
        name={selectedReportTypeName}
        handleFilterChange={handleFilterChange}
        isGenerating={isGenerating}
      />
    ),
    TreatmentDeliveryReport: (
      <ReportForm
        generateReport={generateReport}
        filters={{ ...possibleFilters, clinicians: false, nhsNumber: false }}
        name={selectedReportTypeName}
        handleFilterChange={handleFilterChange}
        isGenerating={isGenerating}
        filtersRestrictions={{
          maxRangeInDays: 14,
        }}
      />
    ),
  };

  const reportMenu = _.entries(reportForms).map(([key]) => (
    <MenuItem
      value={key}
      primaryText={reportNamesLocale.getMsg(lowerFirstLetter(key))}
      key={`report-${key}`}
      onClick={() => otherReportIsSelected(key)}
    />
  ));

  return (
    <PageContent pageTitle={locale.getMsg('reportsTitle')}>
      <div className="flex-page-content content-wrapper">
        <PageHeader>
          <Box className="title-section" display="flex" alignItems="center" px={2} py={1}>
            <Typography variant="h3">{locale.getMsg('reportsTitle')}</Typography>
            {/* <Typography variant="h6">{locale.getMsg('reportsDescription')}</Typography> */}
          </Box>
        </PageHeader>
        <InnerContent isBorderless>
          <div className="full-page">
            <div>
              <div className="filter-section">
                <Box className="filter-section" display="flex" p={1}>
                  <Box style={{ width: '300px' }}>
                    <SelectField
                      floatingLabelText={locale.getMsg('reportType')}
                      value={selectedReportTypeName || ''}
                      onChange={(event, index, value) => selectReportTypeName(value)}
                      floatingLabelStyle={common.floatingLabelStyle}
                      floatingLabelShrinkStyle={common.floatingLabelShrinkStyle}
                      floatingLabelFocusStyle={common.floatingLabelFocusStyle}
                      underlineStyle={common.underlineStyle}
                      style={dropDownStyle.style}
                      iconStyle={dropDownStyle.iconStyle}
                      labelStyle={dropDownStyle.labelStyle}
                      menuStyle={dropDownStyle.menuStyle}
                    >
                      {reportMenu}
                    </SelectField>
                  </Box>
                </Box>
              </div>
              {selectedReportTypeName && reportForms[selectedReportTypeName]}
            </div>
          </div>
        </InnerContent>
      </div>
    </PageContent>
  );
};

ReportSelection.defaultProps = {
  selectedReportTypeName: '',
};

ReportSelection.propTypes = {
  selectedReportTypeName: PropTypes.string,
  selectReportTypeName: PropTypes.func.isRequired,
};

export default ReportSelection;
